import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchProductsByCategory, fetchProductsByCategories } from '../lib/products';
import { getFileUrl } from '../lib/pb';
import '../styles/theme.css';
import { useFavorites } from '../hooks/useFavorites';

export default function ProductList({ categoryId, categoryIds, onCountChange, buildProductUrl, sortMode = 'relevance' }){
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // viewItems keeps displayed items with a status for animations: 'stable' | 'entering' | 'leaving'
  const [viewItems, setViewItems] = useState([]);
  const [lastResultCount, setLastResultCount] = useState(0);
  const navigate = useNavigate();
  const { isFavorite, toggle, setOpen } = useFavorites();

  useEffect(() => {
    let mounted = true;
    async function load(){
      setLoading(true);
      setError('');
      try{
        let res;
        if(Array.isArray(categoryIds) && categoryIds.length){
          res = await fetchProductsByCategories(categoryIds);
        }else{
          res = await fetchProductsByCategory(categoryId);
        }
        if(mounted){
          let list = Array.isArray(res) ? [...res] : [];
          if(sortMode === 'random'){
            for(let i = list.length - 1; i > 0; i--){
              const j = Math.floor(Math.random() * (i + 1));
              [list[i], list[j]] = [list[j], list[i]];
            }
          }else if(sortMode === 'name-asc'){
            list.sort((a,b) => (a.title || '').localeCompare(b.title || ''));
          }else if(sortMode === 'name-desc'){
            list.sort((a,b) => (b.title || '').localeCompare(a.title || ''));
          } // relevance keeps backend order 'order,title'
          setLastResultCount(list.length);
          if(onCountChange){ onCountChange(list.length); }

          // Diff current viewItems vs next list to animate per-card
          setViewItems(prev => {
            const prevMap = new Map(prev.map(p => [p.id, p]));
            const nextIds = new Set(list.map(p => p.id));
            // Keep order from next list; mark new ones as entering, old as stable
            const nextView = list.map(p => {
              const existed = prevMap.get(p.id);
              return existed ? { ...p, status: 'stable' } : { ...p, status: 'entering' };
            });
            // Append leaving items (exist in prev, not in next)
            prev.forEach(p => {
              if(!nextIds.has(p.id)){
                nextView.push({ ...p, status: 'leaving' });
              }
            });
            // Schedule status updates/removals
            // Promote entering -> stable shortly to trigger transition
            setTimeout(() => {
              if(!mounted) return;
              setViewItems(curr => curr.map(it => it.status === 'entering' ? { ...it, status: 'stable' } : it));
            }, 20);
            // Remove leaving after transition duration
            setTimeout(() => {
              if(!mounted) return;
              setViewItems(curr => curr.filter(it => it.status !== 'leaving'));
            }, 260);
            return nextView;
          });
        }
      }catch(e){
        if(mounted){
          const msg = e.message || 'Erreur de chargement';
          setError(msg);
          if(onCountChange){
            onCountChange(0);
          }
        }
      }finally{
        if(mounted){
          setLoading(false);
        }
      }
    }
    load();
    return () => { mounted = false; };
  }, [categoryId, categoryIds, onCountChange, sortMode]);

  if (error) return <div style={{color:'crimson'}}>Erreur: {error}</div>;

  return (
    <div className="product-list-container-signature">
      <div className="product-grid-signature">
        {viewItems.map((p, idx) => {
          const goToDetail = () => {
            if(!p.slug) return;
            const target = typeof buildProductUrl === 'function'
              ? buildProductUrl(p)
              : `/produits/${encodeURIComponent(p.slug)}`;
            if(!target) return;
            navigate(target);
          };
          const isEntering = p.status === 'entering';
          const isLeaving = p.status === 'leaving';
          const staggerDelay = isEntering ? (idx % 10) * 0.05 : 0;

          return (
            <article
              key={p.id}
              className={`product-card-elite shadow-signature ${isEntering ? 'entering' : ''} ${isLeaving ? 'leaving' : ''}`}
              style={{ animationDelay: `${staggerDelay}s` }}
              onClick={goToDetail}
            >
              <div className="product-visual-wrap">
                {Array.isArray(p.images) && p.images[0] ? (
                  <img alt={p.title} src={getFileUrl(p, p.images[0])} />
                ) : (
                  <div className="product-placeholder">✨</div>
                )}
                
                <button
                  type="button"
                  className={`btn-fav-signature ${isFavorite(p.id) ? 'active' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggle(p);
                    setOpen(true);
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill={isFavorite(p.id) ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                    <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 22l7.8-8.6 1-1a5.5 5.5 0 0 0 0-7.8z" />
                  </svg>
                </button>
              </div>

              <div className="product-info-box">
                <div className="product-category-tag">MODÈLE EXCLUSIF</div>
                <h3 className="product-title">{p.title}</h3>
                {p.excerpt && <p className="product-excerpt">{p.excerpt}</p>}
                
                <div className="product-card-footer">
                   <span className="btn-view-pro">Détails techniques <span className="arrow">→</span></span>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      {!loading && lastResultCount === 0 && (
        <div className="product-empty-state">
           <div className="empty-icon">📂</div>
           <p>Aucun produit ne correspond à votre sélection.</p>
           <button className="btn-reset-small" onClick={() => navigate('/catalogue')}>Voir tout le catalogue</button>
        </div>
      )}

      <style>{`
        .product-list-container-signature { width: 100%; }
        .product-grid-signature { 
           display: grid; 
           grid-template-columns: repeat(auto-fill, minmax(210px, 1fr)); 
           gap: 20px; 
        }

        .product-card-elite { 
           background: #fff; border: 1px solid #f1f5f9; border-radius: 20px; 
           overflow: hidden; cursor: pointer; display: flex; flex-direction: column;
           transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
        }
        .product-card-elite:hover { 
           transform: translateY(-8px); border-color: #a37e2c; 
           box-shadow: 0 20px 40px rgba(15, 23, 42, 0.08) !important; 
        }

        .product-visual-wrap { height: 160px; position: relative; overflow: hidden; background: #f8fafc; }
        .product-visual-wrap img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.8s ease; }
        .product-card-elite:hover .product-visual-wrap img { transform: scale(1.08); }
        
        .product-placeholder { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; font-size: 24px; }

        .btn-fav-signature { 
           position: absolute; top: 10px; right: 10px; width: 34px; height: 34px; 
           border-radius: 50%; border: 1px solid rgba(255,255,255,0.2); 
           background: rgba(255,255,255,0.7); backdrop-filter: blur(8px);
           display: flex; align-items: center; justify-content: center; 
           color: #64748b; cursor: pointer; transition: all 0.3s; z-index: 5;
        }
        .btn-fav-signature:hover { transform: scale(1.1); color: #ef4444; background: #fff; }
        .btn-fav-signature.active { color: #ef4444; background: #fff; border-color: #fee2e2; }

        .product-info-box { padding: 15px; display: flex; flex-direction: column; flex-grow: 1; }
        .product-category-tag { font-size: 9px; font-weight: 800; color: #a37e2c; letter-spacing: 1px; text-transform: uppercase; margin-bottom: 6px; }
        .product-title { font-size: 15px; font-weight: 950; color: #0f172a; margin: 0 0 8px 0; line-height: 1.2; }
        .product-excerpt { font-size: 12.5px; color: #64748b; line-height: 1.5; margin-bottom: 15px; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }

        .product-card-footer { margin-top: auto; padding-top: 12px; border-top: 1px solid #f8fafc; }
        .btn-view-pro { font-size: 11px; font-weight: 800; color: #0f172a; display: flex; align-items: center; gap: 6px; transition: all 0.2s; text-transform: uppercase; letter-spacing: 0.5px; }
        .btn-view-pro .arrow { color: #a37e2c; transition: transform 0.2s; }
        .product-card-elite:hover .btn-view-pro .arrow { transform: translateX(3px); }

        /* ANIMATIONS */
        .entering { animation: productFadeIn 0.5s ease forwards; transform: translateY(10px); opacity: 0; }
        .leaving { animation: productFadeOut 0.3s ease forwards; }
        @keyframes productFadeIn { to { transform: translateY(0); opacity: 1; } }
        @keyframes productFadeOut { to { transform: translateY(10px); opacity: 0; } }

        /* EMPTY STATE */
        .product-empty-state { text-align: center; padding: 60px 20px; background: #fdfaf3; border-radius: 32px; border: 1px dashed #e2e8f0; }
        .empty-icon { font-size: 40px; margin-bottom: 15px; opacity: 0.5; }
        .product-empty-state p { font-size: 16px; font-weight: 700; color: #475569; margin-bottom: 20px; }
        .btn-reset-small { 
           background: #a37e2c; color: #fff; border: none; padding: 10px 25px; 
           border-radius: 12px; font-weight: 800; cursor: pointer; transition: all 0.2s;
        }
        .btn-reset-small:hover { transform: translateY(-2px); box-shadow: 0 10px 20px rgba(163, 126, 44, 0.2); }

        @media (max-width: 600px) {
           .product-grid-signature { grid-template-columns: repeat(2, 1fr); gap: 12px; }
           .product-visual-wrap { height: 130px; }
           .product-card-elite { border-radius: 16px; }
           .product-info-box { padding: 12px; }
           .product-title { font-size: 14px; }
           .product-excerpt { display: none; }
        }
      `}</style>
    </div>
  );
}
